import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import type { LinkedWallet } from '../api/auth';
import type { WalletProviderId } from '../types/signing';

// Stored token info for each wallet
interface WalletToken {
  token: string;
  user: User;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /** Wallet app the active session signs with. Xaman-only until multi-wallet Phase 4. */
  walletProvider: WalletProviderId;
  isLoading: boolean;
  error: string | null;

  // Email collection modal state
  emailModalDismissed: boolean;
  emailModalLastShown: number | null;

  // Multi-wallet state
  linkedWallets: LinkedWallet[];
  walletTokens: Record<string, WalletToken>; // address -> { token, user }
  activeWalletAddress: string | null;

  // Actions
  setUser: (user: User, token: string, provider?: WalletProviderId) => void;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  dismissEmailModal: () => void;
  resetEmailModal: () => void;

  // Multi-wallet actions
  setLinkedWallets: (wallets: LinkedWallet[]) => void;
  addLinkedWallet: (wallet: LinkedWallet, token: string, user: User) => void;
  removeLinkedWallet: (address: string) => void;
  storeWalletToken: (address: string, token: string, user: User) => void;
  switchToWallet: (address: string) => boolean;
  getOtherWallets: () => LinkedWallet[];
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      walletProvider: 'xaman',
      isLoading: false,
      error: null,
      emailModalDismissed: false,
      emailModalLastShown: null,
      linkedWallets: [],
      walletTokens: {},
      activeWalletAddress: null,

      setUser: (user, token, provider = 'xaman') => {
        // Store token in localStorage for API requests
        localStorage.setItem('xrpl_marketplace_token', token);
        localStorage.setItem('xrpl_marketplace_user', JSON.stringify(user));

        // Store this wallet's token for later switching
        const walletTokens = { ...get().walletTokens };
        walletTokens[user.xrpl_address] = { token, user };

        set({
          user,
          token,
          isAuthenticated: true,
          walletProvider: provider,
          error: null,
          activeWalletAddress: user.xrpl_address,
          walletTokens,
        });
      },

      updateUser: (updates) =>
        set((state) => {
          if (!state.user) return state;

          const updatedUser = { ...state.user, ...updates };
          localStorage.setItem('xrpl_marketplace_user', JSON.stringify(updatedUser));

          // Also update in walletTokens
          const walletTokens = { ...state.walletTokens };
          if (state.activeWalletAddress && walletTokens[state.activeWalletAddress]) {
            walletTokens[state.activeWalletAddress] = {
              ...walletTokens[state.activeWalletAddress],
              user: updatedUser,
            };
          }

          return {
            user: updatedUser,
            walletTokens,
          };
        }),

      logout: () => {
        localStorage.removeItem('xrpl_marketplace_token');
        localStorage.removeItem('xrpl_marketplace_user');

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
          linkedWallets: [],
          walletTokens: {},
          activeWalletAddress: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      clearError: () => set({ error: null }),

      dismissEmailModal: () => set({
        emailModalDismissed: true,
        emailModalLastShown: Date.now(),
      }),

      resetEmailModal: () => set({
        emailModalDismissed: false,
        emailModalLastShown: null,
      }),

      // Multi-wallet actions

      setLinkedWallets: (wallets) => set({ linkedWallets: wallets }),

      addLinkedWallet: (wallet, token, user) =>
        set((state) => {
          // Add to linked wallets if not already present
          const existingIndex = state.linkedWallets.findIndex(
            (w) => w.xrpl_address === wallet.xrpl_address
          );
          const linkedWallets =
            existingIndex >= 0
              ? state.linkedWallets
              : [...state.linkedWallets, wallet];

          // Store the token for this wallet
          const walletTokens = { ...state.walletTokens };
          walletTokens[wallet.xrpl_address] = { token, user };

          return { linkedWallets, walletTokens };
        }),

      removeLinkedWallet: (address) =>
        set((state) => {
          const linkedWallets = state.linkedWallets.filter(
            (w) => w.xrpl_address !== address
          );
          const walletTokens = { ...state.walletTokens };
          delete walletTokens[address];

          return { linkedWallets, walletTokens };
        }),

      storeWalletToken: (address, token, user) =>
        set((state) => {
          const walletTokens = { ...state.walletTokens };
          walletTokens[address] = { token, user };
          return { walletTokens };
        }),

      switchToWallet: (address) => {
        const state = get();
        const walletData = state.walletTokens[address];

        if (!walletData) {
          // No stored token for this wallet
          return false;
        }

        // Update localStorage
        localStorage.setItem('xrpl_marketplace_token', walletData.token);
        localStorage.setItem('xrpl_marketplace_user', JSON.stringify(walletData.user));

        set({
          user: walletData.user,
          token: walletData.token,
          activeWalletAddress: address,
        });

        return true;
      },

      getOtherWallets: () => {
        const state = get();
        if (!state.activeWalletAddress) return state.linkedWallets;
        return state.linkedWallets.filter(
          (w) => w.xrpl_address !== state.activeWalletAddress
        );
      },
    }),
    {
      name: 'xrpl-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        walletProvider: state.walletProvider,
        emailModalDismissed: state.emailModalDismissed,
        emailModalLastShown: state.emailModalLastShown,
        linkedWallets: state.linkedWallets,
        walletTokens: state.walletTokens,
        activeWalletAddress: state.activeWalletAddress,
      }),
    }
  )
);
